import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Link } from '@tanstack/react-router'
import { FileText, User } from 'lucide-react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface ReportCardProps {
  report: {
    id: string
    content: string
    created_at: string
    report_type: string
    patient: {
      id: string
      full_name: string
    }
    sentiment_score?: number | null
  }
}

const sentimentColors: Record<string, string> = {
  positive: 'bg-green-100 text-green-800',
  neutral: 'bg-gray-100 text-gray-800',
  negative: 'bg-red-100 text-red-800',
  mixed: 'bg-yellow-100 text-yellow-800',
}

const sentimentLabels: Record<string, string> = {
  positive: 'Positivo',
  neutral: 'Neutro',
  negative: 'Negativo',
  mixed: 'Misto',
}

function getSentimentFromScore(score: number | null | undefined): string | null {
  if (score === null || score === undefined) return null
  if (score >= 0.6) return 'positive'
  if (score >= 0.4) return 'neutral'
  if (score >= 0.2) return 'mixed'
  return 'negative'
}

export function ReportCard({ report }: ReportCardProps) {
  const sentiment = getSentimentFromScore(report.sentiment_score)

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-muted rounded-lg">
            <FileText className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <Link
                to="/patients/$patientId"
                params={{ patientId: report.patient.id }}
                className="flex items-center gap-1 text-sm font-medium hover:underline"
              >
                <User className="h-3 w-3" />
                {report.patient.full_name}
              </Link>
              <span className="text-sm text-muted-foreground">•</span>
              <span className="text-sm text-muted-foreground">
                {format(new Date(report.created_at), "d 'de' MMMM, yyyy 'as' HH:mm", {
                  locale: ptBR,
                })}
              </span>
              {sentiment && (
                <Badge className={sentimentColors[sentiment] ?? 'bg-gray-100 text-gray-800'}>
                  {sentimentLabels[sentiment] ?? sentiment}
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-wrap">
              {report.content}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Tipo: {report.report_type}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
